import { useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { testimonialsData } from './testimonials-data';
import './testimonials-scroll.css';

export const TestimonialsScroll = () => {
  const scrollListRef = useRef<HTMLUListElement>(null);
  const progressBarRef = useRef<HTMLElement>(null);
  const prevButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);

  // Helper to highlight the pull quote within the full quote
  const highlightPull = (quote: string, pull: string) => {
    const cleanPull = pull.replace(/^[""]|[""]$/g, '');
    const parts = quote.split(cleanPull);

    if (parts.length === 2) {
      return (
        <>
          {parts[0]}
          <mark>{cleanPull}</mark>
          {parts[1]}
        </>
      );
    }
    return quote;
  };

  useEffect(() => {
    const ul = scrollListRef.current;
    const bar = progressBarRef.current;
    const prev = prevButtonRef.current;
    const next = nextButtonRef.current;

    if (!ul || !bar || !prev || !next) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const getStepWidth = () => {
      const firstCard = ul.querySelector('.q');
      return firstCard ? firstCard.getBoundingClientRect().width + 40 : 400;
    };

    const updateScrollState = () => {
      const maxScroll = ul.scrollWidth - ul.clientWidth;
      const progressWidth = maxScroll > 0
        ? Math.max(12, (ul.clientWidth / ul.scrollWidth) * 100)
        : 100;

      bar.style.width = `${progressWidth}%`;
      bar.style.left = maxScroll > 0
        ? `${(ul.scrollLeft / maxScroll) * (100 - progressWidth)}%`
        : '0%';

      prev.disabled = ul.scrollLeft <= 2;
      next.disabled = ul.scrollLeft >= maxScroll - 2;
    };

    // Arrow button handlers
    const handlePrevClick = () => {
      ul.scrollBy({
        left: -getStepWidth(),
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    };

    const handleNextClick = () => {
      ul.scrollBy({
        left: getStepWidth(),
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    };

    // Click and drag scrolling
    let isDragging = false;
    let startX = 0;
    let scrollLeft = 0;

    const handleMouseDown = (e: MouseEvent) => {
      isDragging = true;
      startX = e.pageX;
      scrollLeft = ul.scrollLeft;
      ul.style.scrollSnapType = 'none';
      ul.style.cursor = 'grabbing';
    };

    const handleMouseUp = () => {
      if (!isDragging) return;
      isDragging = false;
      ul.style.scrollSnapType = '';
      ul.style.cursor = '';
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      ul.scrollLeft = scrollLeft - (e.pageX - startX);
    };

    // Attach listeners
    prev.addEventListener('click', handlePrevClick);
    next.addEventListener('click', handleNextClick);
    ul.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    ul.addEventListener('mousemove', handleMouseMove);
    ul.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    // Initial update
    updateScrollState();

    // Cleanup
    return () => {
      prev.removeEventListener('click', handlePrevClick);
      next.removeEventListener('click', handleNextClick);
      ul.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      ul.removeEventListener('mousemove', handleMouseMove);
      ul.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, []);

  return (
    <section className="rl-ta" aria-labelledby="rl-ta-title">
      <div className="top">
        <div>
          <span className="eb">Testimonials</span>
          <h2 id="rl-ta-title">What colleagues <em>say.</em></h2>
        </div>
        <div className="nav">
          <button
            ref={prevButtonRef}
            type="button"
            aria-label="Previous testimonials"
          >
            <ChevronLeft size={20} color="#D9442B" />
          </button>
          <button
            ref={nextButtonRef}
            type="button"
            aria-label="Next testimonials"
          >
            <ChevronRight size={20} color="#D9442B" />
          </button>
        </div>
      </div>

      <ul ref={scrollListRef} tabIndex={0} aria-label="Testimonials, scroll sideways">
        {testimonialsData.map((testimonial, index) => (
          <li key={index} className="q">
            <span className="n">{String(index + 1).padStart(2, '0')}</span>
            <p className="pull">"{testimonial.pull}"</p>
            <blockquote>
              {highlightPull(testimonial.quote, testimonial.pull)}
            </blockquote>
            <div className="who">
              <span className="ini" aria-hidden="true">
                {testimonial.initials}
              </span>
              <div>
                <b>{testimonial.name}</b>
                <span>{testimonial.role}</span>
              </div>
            </div>
          </li>
        ))}
      </ul>

      <div className="bar" aria-hidden="true">
        <div>
          <i ref={progressBarRef}></i>
        </div>
      </div>
    </section>
  );
};
